import { css } from '@linaria/core'
import { type FC, useMemo } from 'react'
import type { AppProductGroup, ProductPath } from '#app/types/AppTypes'
import { Icon } from '#components/Icon'
import { fragments, style } from '#styles'
import { themeColors } from '#styles/themes'
import type { ComparisonDisplayMode, ComparisonTableContext } from './ComparisonTableContext'
import type { ConfigurationDiff } from './useConfigurationDifference'
import type { ComparisonDataEntry, GroupDataEntry } from './useLinearizeComparisonData'

const treeClass = css`
  display: flex;
  flex: 1;
  flex-direction: column;
  user-select: none;

  > ._group {
    cursor: pointer;
    padding: 8px 16px 8px 24px;
    align-self: stretch;
    align-items: center;

    display: flex;
    flex-direction: row;
    gap: 6px;

    color: ${style.themeColors.textImportant};
    ${fragments.textStyle.heading.s.regular}

    transition: ease 0.25s background-color;

    > ._title {
      ${fragments.textStyle.button.m}
      letter-spacing: 1px;
    }

    &:hover {
      background: ${themeColors.backgroundBoxedHover}
    }
  }

  > ._subgroup {
    cursor: pointer;
    padding: 0px 15px 0px 24px;
    align-self: stretch;

    display: flex; 
    flex-direction: row;
    gap: 6px;
    align-items: center;

    transition: ease 0.25s background-color;
    &:hover {
      background: ${themeColors.backgroundBoxedHover}
    }

    > ._line {
      height: 100%;
      width: 18px;
      background: linear-gradient(${style.themeColors.textImportant}, ${style.themeColors.textImportant}) no-repeat center/1px 100%;
    }

    > ._title {
      padding: 8px 0;
      flex: 1;
      color: ${style.themeColors.textDefault};
      ${fragments.textStyle.nav.regular}
    }

    > ._change-count {
      height: 19px;
      aspect-ratio: 1;
      border-radius: 24px;
      color: ${style.themeColors.textPrimary};
      background: ${style.themeColors.backgroundAccent};

      display: flex;
      justify-content: center;
      align-items: center;

      // important: keeps the text center, because the line height might push the glyph up/down
      line-height: 100%;

      > ._value {
        padding: 0px 6px;
      }

    }
  }
`

const GroupEntry: FC<{
  entry: GroupDataEntry
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
}> = ({ entry, scrollTo }) => {
  const baseEntry = entry.base ?? entry.comparison
  if (!baseEntry) {
    return undefined
  }

  return (
    <div className="_group" onClick={() => scrollTo(entry)}>
      <Icon iconUrl={baseEntry.iconUrl} />
      <span className="_title">{baseEntry.title}</span>
    </div>
  )
}

const SubgroupEntry: FC<{
  entry: GroupDataEntry
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  countsByGroupPath: Record<ProductPath, number | undefined>
}> = ({ entry, scrollTo, countsByGroupPath }) => {
  const affectedFamilyCount = countsByGroupPath[entry.id] ?? 0

  const baseEntry = entry.base ?? entry.comparison
  if (!baseEntry) {
    return undefined
  }

  return (
    <div
      className="_subgroup"
      onClick={() => {
        scrollTo(entry)
      }}
    >
      <div className="_line" />
      <span className="_title">{baseEntry.title}</span>
      {affectedFamilyCount > 0 && (
        <div className="_change-count">
          <span className="_value">{affectedFamilyCount}</span>
        </div>
      )}
    </div>
  )
}

export const ComparisonTree: FC<{
  scrollTo: ComparisonTableContext['virtualizer']['scrollTo']
  displayedEntries: ComparisonDataEntry[]
  displayMode: ComparisonDisplayMode
  diff: ConfigurationDiff | undefined
  selectedEntries: Partial<Record<ProductPath, boolean>>
}> = ({ scrollTo, displayedEntries }) => {
  const countsByGroupPath = useMemo(() => {
    const counts: Record<ProductPath, number | undefined> = {}

    let activeGroup: AppProductGroup | undefined
    for (const entry of displayedEntries) {
      switch (entry.type) {
        case 'group': {
          activeGroup = entry.base ?? entry.comparison
          break
        }
        case 'family': {
          if (activeGroup && (entry.affected || entry.missing)) {
            if (activeGroup.parentGroup) {
              counts[activeGroup.parentGroup.path] = (counts[activeGroup.parentGroup.path] ?? 0) + 1
            }
            counts[activeGroup.path] = (counts[activeGroup.path] ?? 0) + 1
          }
        }
      }
    }

    return counts
  }, [displayedEntries])

  return (
    <div className={treeClass}>
      {displayedEntries.map((entry) => {
        switch (entry.type) {
          case 'family':
            return undefined
          case 'group': {
            // @TODO: investigate alternative for type coercion, because one of the two _has to_ exists
            const group = entry.base ?? entry.comparison
            if (!group) {
              return undefined
            }

            return group.parentGroup ? (
              <SubgroupEntry key={entry.id} entry={entry} scrollTo={scrollTo} countsByGroupPath={countsByGroupPath} />
            ) : (
              <GroupEntry key={entry.id} entry={entry} scrollTo={scrollTo} />
            )
          }
        }
        return undefined
      })}
    </div>
  )
}
